import { useCallback, useEffect, useMemo, useState } from 'react';
import { PerDayType, DashboardData, UserSettings } from '../Types';
import { RootState, useAppSelector } from '../store';
import { dashboardService } from '../Services/dashboardService';

const useAdminDashboard = () => {
    const settings = useAppSelector((state: RootState) => state.settings);
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);
    const windowWidth = useAppSelector((state: RootState) => state.environment.windowWidth);

    const selectedLanguage = useAppSelector((state: RootState) => state.i18n.selectedLanguage);
    const showUserStatistics = useAppSelector((state: RootState) => state.environment.showUserStatistics);

    const [dashboardData, setDashboardData] = useState<DashboardData>({
        licencesUsed: -1,
        licencesFree: -1,
        totalCosts: -1,
        totalCredit: -1,
        totalCostsTimeRange: -1 as number,
        totalCostsAvgTimeRange: -1,
        perDay: [] as PerDayType[],
        previousTotalCostsAvgTimeRange: -1,
        previousTotalCostsTimeRange: -1 as number,
        balance: -1,
        totalDlpTrackCount: {
            anonymizedCount: -1,
            sensitiveCount: -1,
            nonsensitiveCount: -1,
            recognizedSensitiveCount: -1,
        },
        previousTotalDlpTrackCount: {
            anonymizedCount: -1,
            sensitiveCount: -1,
            nonsensitiveCount: -1,
            recognizedSensitiveCount: -1,
        },
    });

    const [timeRange, setTimeRange] = useState<any>({
        from: new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0),
        to: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 23, 59, 59),
    });
    const [previousTimeRange, setPreviousTimeRange] = useState<any>({
        from: new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0),
        to: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 23, 59, 59),
    });
    const [dashboardloading, setDashboardLoading] = useState({
        licences: false,
        totalCosts: false,
        totalCredit: false,
        timeRangeCosts: false,
        timeRangeAvgCosts: false,
        previousTimeRangeCosts: false,
        previousTimeRangeAvgCosts: false,
        costsPerDay: false,
        totalDlpTrackCount: false,
        previousTotalDlpTrackCount: false,
    });

    const getPreviousTimeRange = (timeRange: any): any => {
        if (!timeRange.from || !timeRange.to) {
            return {
                from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
                to: new Date(),
            };
        }
        const deltaMilliseconds = timeRange.to.getTime() - timeRange.from.getTime();
        const deltaDays = Math.ceil(deltaMilliseconds / (1000 * 60 * 60 * 24));

        const previousFrom = new Date(timeRange.from);
        const previousTo = new Date(timeRange.to);

        previousFrom.setDate(timeRange.from.getDate() - deltaDays);
        previousTo.setDate(timeRange.to.getDate() - deltaDays);

        return {
            from: previousFrom,
            to: previousTo,
        };
    };

    const dateTimeConverter = useCallback((dateTime: Date) => {
        return dateTime.toISOString().replace(/\.\d{3}/, '');
    }, []);

    const fetchData = useCallback(async () => {
        try {
            setDashboardLoading((prev) => ({
                ...prev,
                licences: true,
                totalCosts: true,
                totalCredit: true,
                timeRangeCosts: true,
                timeRangeAvgCosts: true,
                previousTimeRangeCosts: true,
                previousTimeRangeAvgCosts: true,
                costsPerDay: true,
                totalDlpTrackCount: true,
                previousTotalDlpTrackCount: true,
            }));

            if (timeRange.from && timeRange.to) {
                const results = await Promise.allSettled([
                    dashboardService.getLicences(demo),
                    dashboardService.getTotalCost(demo),
                    dashboardService.getTotalCredit(demo),
                    dashboardService.getTotalCostByDate(
                        demo,
                        dateTimeConverter(timeRange.from),
                        dateTimeConverter(timeRange.to),
                    ),
                    dashboardService.getAverageCostByDate(
                        demo,
                        dateTimeConverter(timeRange.from),
                        dateTimeConverter(timeRange.to),
                    ),
                    dashboardService.getCostPerDay(
                        demo,
                        dateTimeConverter(timeRange.from),
                        dateTimeConverter(timeRange.to),
                    ),
                    previousTimeRange.from && previousTimeRange.to
                        ? dashboardService.getTotalCostByDate(
                              demo,
                              dateTimeConverter(previousTimeRange.from),
                              dateTimeConverter(previousTimeRange.to),
                          )
                        : { data: 0 },
                    previousTimeRange.from && previousTimeRange.to
                        ? dashboardService.getAverageCostByDate(
                              demo,
                              dateTimeConverter(previousTimeRange.from),
                              dateTimeConverter(previousTimeRange.to),
                          )
                        : { data: 0 },
                    dashboardService.getTotalDlpTrackCount(
                        demo,
                        dateTimeConverter(timeRange.from),
                        dateTimeConverter(timeRange.to),
                    ),
                    previousTimeRange.from && previousTimeRange.to
                        ? dashboardService.getTotalDlpTrackCount(
                              demo,
                              dateTimeConverter(previousTimeRange.from),
                              dateTimeConverter(previousTimeRange.to),
                          )
                        : {
                              data: {
                                  anonymizedCount: -1,
                                  sensitiveCount: -1,
                                  nonsensitiveCount: -1,
                                  recognizedSensitiveCount: -1,
                              },
                          },
                ]);

                const [
                    licencesResponse,
                    totalCostsResponse,
                    totalCreditResponse,
                    totalCostsTimeRangeResponse,
                    totalCostsAvgTimeRangeResponse,
                    perDayResponse,
                    previousTotalCostsTimeRangeResponse,
                    previousTotalCostsAvgTimeRangeResponse,
                    totalDlpTrackCountResponse,
                    previousTotalDlpTrackCountResponse,
                ] = results;

                setDashboardData((prev) => ({
                    ...prev,
                    licencesUsed:
                        licencesResponse.status === 'fulfilled' ? licencesResponse.value.data.used : prev.licencesUsed,
                    licencesFree:
                        licencesResponse.status === 'fulfilled' ? licencesResponse.value.data.free : prev.licencesFree,
                    totalCosts:
                        totalCostsResponse.status === 'fulfilled' ? totalCostsResponse.value.data : prev.totalCosts,
                    totalCredit:
                        totalCreditResponse.status === 'fulfilled' ? totalCreditResponse.value.data : prev.totalCredit,
                    totalCostsTimeRange:
                        totalCostsTimeRangeResponse.status === 'fulfilled'
                            ? totalCostsTimeRangeResponse.value.data
                            : prev.totalCostsTimeRange,
                    totalCostsAvgTimeRange:
                        totalCostsAvgTimeRangeResponse.status === 'fulfilled'
                            ? totalCostsAvgTimeRangeResponse.value.data
                            : prev.totalCostsAvgTimeRange,
                    perDay: perDayResponse.status === 'fulfilled' ? perDayResponse.value.data : prev.perDay,
                    previousTotalCostsTimeRange:
                        previousTotalCostsTimeRangeResponse?.status === 'fulfilled'
                            ? previousTotalCostsTimeRangeResponse?.value?.data
                            : prev.previousTotalCostsTimeRange,
                    previousTotalCostsAvgTimeRange:
                        previousTotalCostsAvgTimeRangeResponse?.status === 'fulfilled'
                            ? previousTotalCostsAvgTimeRangeResponse?.value?.data
                            : prev.previousTotalCostsAvgTimeRange,
                    balance:
                        totalCreditResponse.status === 'fulfilled' && totalCostsResponse.status === 'fulfilled'
                            ? totalCreditResponse.value.data - totalCostsResponse.value.data
                            : prev.balance,
                    totalDlpTrackCount:
                        totalDlpTrackCountResponse.status === 'fulfilled'
                            ? totalDlpTrackCountResponse.value.data
                            : prev.totalDlpTrackCount,
                    previousTotalDlpTrackCount:
                        previousTotalDlpTrackCountResponse.status === 'fulfilled'
                            ? previousTotalDlpTrackCountResponse.value.data
                            : prev.previousTotalDlpTrackCount,
                }));

                setDashboardLoading((prev) => ({
                    ...prev,
                    licences: licencesResponse.status === 'fulfilled' ? false : true,
                    totalCosts: totalCostsResponse.status === 'fulfilled' ? false : true,
                    totalCredit: totalCreditResponse.status === 'fulfilled' ? false : true,
                    timeRangeCosts: totalCostsTimeRangeResponse.status === 'fulfilled' ? false : true,
                    timeRangeAvgCosts: totalCostsAvgTimeRangeResponse.status === 'fulfilled' ? false : true,
                    previousTimeRangeCosts: previousTotalCostsTimeRangeResponse.status === 'fulfilled' ? false : true,
                    previousTimeRangeAvgCosts:
                        previousTotalCostsAvgTimeRangeResponse.status === 'fulfilled' ? false : true,
                    costsPerDay: perDayResponse.status === 'fulfilled' ? false : true,
                    totalDlpTrackCount: totalDlpTrackCountResponse.status !== 'fulfilled',
                    previousDlpTrackCount: previousTotalDlpTrackCountResponse.status !== 'fulfilled',
                }));
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setDashboardLoading((prev) => ({
                ...prev,
                licences: false,
                totalCosts: false,
                totalCredit: false,
                timeRangeCosts: false,
                timeRangeAvgCosts: false,
                previousTimeRangeCosts: false,
                previousTimeRangeAvgCosts: false,
                costsPerDay: false,
                totalDlpTrackCount: false,
                previousTotalDlpTrackCount: false,
            }));
        }
    }, [
        demo,
        showUserStatistics,
        timeRange.from,
        timeRange.to,
        previousTimeRange.from,
        previousTimeRange.to,
        dateTimeConverter,
    ]);

    const valueFormatter = useCallback(
        (number: number) => {
            return `$ ${new Intl.NumberFormat(selectedLanguage, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
                .format(Number(number))
                .toString()}`;
        },
        [selectedLanguage],
    );

    const getLicenceBadgeText = useCallback((licencesFree: number, i18n: any) => {
        if (licencesFree < 0) {
            return i18n.translate({
                id: 'licences.exceeded',
                sourceName: 'Dashboard',
                params: { licencesExceeded: Math.abs(licencesFree) },
            });
        }
        if (licencesFree === 0) {
            return i18n.translate({
                id: 'licences.noLicences',
                sourceName: 'Dashboard',
            });
        }
        if (licencesFree === 1) {
            return i18n.translate({
                id: 'licences.oneLicences',
                sourceName: 'Dashboard',
            });
        }
        if (licencesFree > 1) {
            return i18n.translate({
                id: 'licences.moreLicences',
                sourceName: 'Dashboard',
                params: { licencesFree: licencesFree },
            });
        }
        return null;
    }, []);

    const getLicenceBadgeVariant = useCallback((licencesFree: number) => {
        if (licencesFree < 0) {
            return 'danger';
        }
        if (licencesFree >= 2) {
            return 'success';
        }
        if (licencesFree === 1) {
            return 'warning';
        }
        return 'danger';
    }, []);

    const setTimeRangeWithTime = useCallback((value: any) => {
        value.from?.setHours(0, 0, 0, 0);
        if (typeof value.to === 'undefined') {
            value.to = new Date(value.from ?? new Date());
        }
        value.to?.setHours(23, 59, 59, 999);
        setTimeRange(value);
    }, []);

    const exportData = useCallback(() => {
        if (timeRange.from === undefined || timeRange.to === undefined) return;
        dashboardService
            .getExport(demo, dateTimeConverter(timeRange.from), dateTimeConverter(timeRange.to))
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'export.csv');
                document.body.appendChild(link);
                link.click();
            });
    }, [demo, timeRange.from, timeRange.to, dateTimeConverter]);

    useEffect(() => {
        if (userSettings.isLoggedIn) {
            fetchData();
        }
    }, [userSettings.isLoggedIn, fetchData]);

    useEffect(() => {
        setPreviousTimeRange(getPreviousTimeRange(timeRange));
    }, [timeRange]);

    const DashboardActions = useMemo(
        () => ({
            fetchData,
            valueFormatter,
            setTimeRangeWithTime,
            exportData,
            getLicenceBadgeText,
            getLicenceBadgeVariant,
        }),
        [fetchData, valueFormatter, setTimeRangeWithTime, exportData, getLicenceBadgeText, getLicenceBadgeVariant],
    );

    return {
        DashboardActions,
        dashboardloading,
        timeRange,
        selectedLanguage,
        i18n,
        windowWidth,
        dashboardData,
        showUserStatistics,
        prePaidActive: settings.prePaidActive,
        getCostsPerGroup: useMemo(
            () => (page: number, itemsPerPage: number, _: any, query?: string | null) => {
                if (timeRange.from && timeRange.to) {
                    return dashboardService.getCostsPerGroup(
                        demo,
                        dateTimeConverter(timeRange.from),
                        dateTimeConverter(timeRange.to),
                        page,
                        itemsPerPage,
                        query,
                    );
                }
            },
            [demo, dateTimeConverter, timeRange.from, timeRange.to],
        ),
        getCostsPerUser: useMemo(
            () => (page: number, itemsPerPage: number, _: any, query?: string | null) => {
                if (timeRange.from && timeRange.to) {
                    return dashboardService.getCostsPerUser(
                        demo,
                        dateTimeConverter(timeRange.from),
                        dateTimeConverter(timeRange.to),
                        page,
                        itemsPerPage,
                        query,
                    );
                }
            },
            [demo, dateTimeConverter, timeRange.from, timeRange.to],
        ),
        getCostsPerAssistant: useMemo(
            () => (page: number, itemsPerPage: number, _: any, query?: string | null) => {
                if (timeRange.from && timeRange.to) {
                    return dashboardService.getCostsPerAssistant(
                        demo,
                        dateTimeConverter(timeRange.from),
                        dateTimeConverter(timeRange.to),
                        page,
                        itemsPerPage,
                        query,
                    );
                }
            },
            [demo, dateTimeConverter, timeRange.from, timeRange.to],
        ),
        getCostsPerChatModel: useMemo(
            () => (page: number, itemsPerPage: number, _: any, query?: string | null) => {
                if (timeRange.from && timeRange.to) {
                    return dashboardService.getCostsPerChatModel(
                        demo,
                        dateTimeConverter(timeRange.from),
                        dateTimeConverter(timeRange.to),
                        page,
                        itemsPerPage,
                        query,
                    );
                }
            },
            [demo, dateTimeConverter, timeRange.from, timeRange.to],
        ),
        getCostsPerEmbeddingModel: useMemo(
            () => (page: number, itemsPerPage: number, _: any, query?: string | null) => {
                if (timeRange.from && timeRange.to) {
                    return dashboardService.getCostsPerEmbeddingModel(
                        demo,
                        dateTimeConverter(timeRange.from),
                        dateTimeConverter(timeRange.to),
                        page,
                        itemsPerPage,
                        query,
                    );
                }
            },
            [demo, dateTimeConverter, timeRange.from, timeRange.to],
        ),
        getCostsPerVoiceModel: useMemo(
            () => (page: number, itemsPerPage: number, _: any, query?: string | null) => {
                if (timeRange.from && timeRange.to) {
                    return dashboardService.getCostsPerVoiceModel(
                        demo,
                        dateTimeConverter(timeRange.from),
                        dateTimeConverter(timeRange.to),
                        page,
                        itemsPerPage,
                        query,
                    );
                }
            },
            [demo, dateTimeConverter, timeRange.from, timeRange.to],
        ),
        getCostsPerPromptFunction: useMemo(
            () => (page: number, itemsPerPage: number, _: any, query?: string | null) => {
                if (timeRange.from && timeRange.to) {
                    return dashboardService.getCostsPerPromptFunction(
                        demo,
                        dateTimeConverter(timeRange.from),
                        dateTimeConverter(timeRange.to),
                        page,
                        itemsPerPage,
                        query,
                    );
                }
            },
            [demo, dateTimeConverter, timeRange.from, timeRange.to],
        ),
    };
};

export default useAdminDashboard;
